/**
 * SellPriceUI
 *
 * Compound price cell/field: output amount, rate percent, and the detailed
 * composite breakdown popover. Consumers: the `sellPrice` field renderer and the
 * ArTable cell renderer for the same type.
 *
 * The only form-store leaf it reads is `formType.data.docId`, needed to resolve
 * the org's visibility types for the current form type. `useFormStore()` is
 * banned here: this renders once per table cell, and that hook's spread of
 * `formVersion.data` re-rendered every price cell on each keystroke (SYD-2252).
 */

import { FC, useState, useMemo, type CSSProperties, type FocusEvent } from 'react';
import { Input, theme, Popover, Button } from 'antd';
import { CaretUpOutlined, CaretDownOutlined, MinusOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import FlexBox from '@/components/atoms/FlexBox';
import { isCompoundCellChangePayload } from '#types/System.Artifacts.types';
import type { SellPriceLabels } from '#types/System.types';
import type { SellPriceCellPayload } from './types';
import { useSellPriceUI } from './useSellPriceUI';
import DetailedCompositePriceForm from './DetailedCompositePriceForm';
import { formStore } from '@/store';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import { useVisibilityMode } from '@/use/useVisibilityMode';
import { useOrganizationParameters } from '../../../../use/useOrganizationParameters';
import type { VisibilityType } from '#types/System.types';
import {
  commitDecimalString,
  liveParsedDecimal,
  stripGroupingFromAmountDisplay,
} from './decimalDraftInput';

type SellPriceUIProps = {
  /** Cell value: compound payload { calcOutput, basePrice, ... } or unknown. */
  value?: unknown;
  /** Callback invoked when any value changes. */
  onChange?: (payload: SellPriceCellPayload) => void;
  /** Whether the field is disabled. */
  disabled?: boolean;
  /** Validation status for the field. */
  status?: '' | 'error' | 'warning' | undefined;
  /** Display-only mode for aggregations - hides all controls, shows only the value. */
  displayOnly?: boolean;
  /** Label overrides resolved from column → field type hierarchy. */
  sellPriceLabels?: SellPriceLabels;
  /** Fallback formulaTypeId from field type config (used when cell data has no formulaTypeId). */
  defaultFormulaTypeId?: string;
  /** Cell-level focus gate. In ArTable this opens the bulk-edit confirmation modal. */
  onFocus?: () => void;
  /** Cell-level blur gate. In ArTable this clears the cell editing/preview state. */
  onBlur?: () => void;
  [key: string]: unknown;
};

const SellPriceUI: FC<SellPriceUIProps> = ({
  value,
  onChange = () => {},
  disabled = false,
  status,
  displayOnly = false,
  sellPriceLabels,
  defaultFormulaTypeId,
  onFocus,
  onBlur,
  ...props
}) => {
  const { style } = props as { style?: CSSProperties };
  const valid = isCompoundCellChangePayload(value);
  const v = valid ? (value as Record<string, unknown>) : {};
  const fields = (v.fields && typeof v.fields === 'object' ? v.fields : {}) as Record<string, Record<string, unknown>>;
  const {
    calcOutput,
    basePrice,
    ratePercent,
    caretDirection,
    labels,
    handleClear,
    handleOutputChange,
    handleRatePercentChange,
  } = useSellPriceUI({
    basePrice: typeof fields.basePrice?.value === 'number' ? fields.basePrice.value : 0,
    ratePercent: typeof fields.ratePercent?.value === 'number' ? fields.ratePercent.value : 0,
    formulaTypeId: typeof fields.formulaTypeId?.value === 'string' ? fields.formulaTypeId.value : (defaultFormulaTypeId || ''),
    sellPrice: typeof fields.sellPrice?.value === 'number' ? fields.sellPrice.value : 0,
    sellPriceLabels,
    onChange,
  });
  const { isPdfExportMode } = usePdfExportContext();
  const formTypeId = useSnapshot(formStore.state).formType.data?.docId;
  const visibilityMode = useVisibilityMode();
  const { getVisibilityTypesForFormType } = useOrganizationParameters();

  const visibilityMetadata = useMemo((): Map<string, VisibilityType> => {
    const types = getVisibilityTypesForFormType(formTypeId);
    return new Map(types.map(t => [t.id, t]));
  }, [getVisibilityTypesForFormType, formTypeId]);

  const showOperatorFields = useMemo(() => {
    if (isPdfExportMode) return false;
    if (displayOnly) return false;
    if (visibilityMode === 'pdfview') return false;
    const currentView = visibilityMetadata.get(visibilityMode);
    return !(currentView?.isPdfExport === true);
  }, [isPdfExportMode, displayOnly, visibilityMode, visibilityMetadata]);

  const { token } = theme.useToken();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [draftSell, setDraftSell] = useState<string | null>(null);
  const [draftRate, setDraftRate] = useState<string | null>(null);

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return '';
    const num = Number(amount);
    if (!Number.isFinite(num)) return '';
    const hasDecimals = num % 1 !== 0;
    const formattedValue = hasDecimals ? num.toFixed(2) : num.toString();
    return formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const sellBlurredDisplay = isPdfExportMode
    ? `$ ${formatCurrency(calcOutput)}`
    : formatCurrency(calcOutput);
  const rateBlurredDisplay = ratePercent.toFixed(2);

  const handleCellFocus = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onFocus?.();
  };
  const handleCellBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onBlur?.();
  };

  return (
    <FlexBox
      stretch
      gap={0}
      onFocus={handleCellFocus}
      onBlur={handleCellBlur}
      style={{
        ...style,
      }}
    >
      <Input
        prefix={isPdfExportMode ? undefined : '$'}
        value={draftSell === null ? sellBlurredDisplay : draftSell}
        onFocus={() => {
          setDraftSell(stripGroupingFromAmountDisplay(formatCurrency(calcOutput)));
        }}
        onChange={(e) => {
          const next = e.target.value;
          setDraftSell(next);
          const live = liveParsedDecimal(next);
          if (live !== undefined) handleOutputChange(live);
        }}
        onBlur={() => {
          handleOutputChange(commitDecimalString(draftSell ?? ''));
          setDraftSell(null);
        }}
        disabled={disabled}
        status={status}
        style={{
          flex: 1.5,
          minWidth: 50,
          paddingRight: 0,
          fontVariantNumeric: 'tabular-nums',
        }}
        styles={{
          input: {
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          },
        }}
        variant="borderless"
        aria-label="sellprice-sellprice-input"
      />
      {showOperatorFields && (
        <Input
          suffix="%"
          value={draftRate === null ? rateBlurredDisplay : draftRate}
          onFocus={() => {
            setDraftRate(rateBlurredDisplay);
          }}
          onChange={(e) => {
            const next = e.target.value;
            setDraftRate(next);
            const live = liveParsedDecimal(next);
            if (live !== undefined) handleRatePercentChange(live);
          }}
          onBlur={() => {
            handleRatePercentChange(commitDecimalString(draftRate ?? ''));
            setDraftRate(null);
          }}
          disabled={disabled}
          status={status}
          style={{
            flex: 1,
            minWidth: 80,
            paddingLeft: 0,
            paddingRight: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
          styles={{
            input: {
              color: token.colorTextTertiary,
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'right',
            },
          }}
          variant="borderless"
          aria-label="sellprice-percentage-input"
        />
      )}
      {showOperatorFields && (
        <Popover
          content={
            <DetailedCompositePriceForm
              basePrice={basePrice}
              calcOutput={calcOutput}
              ratePercent={ratePercent}
              caretDirection={caretDirection}
              disabled={disabled}
              status={status}
              labels={labels}
              onOutputChange={handleOutputChange}
              onRatePercentChange={handleRatePercentChange}
              onClear={handleClear}
              showOperatorFields={showOperatorFields}
            />
          }
          trigger="click"
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          placement="bottomRight"
        >
          <Button
            type="text"
            style={{ width: 32 }}
            icon={
              caretDirection === 'neutral' ? (
                <MinusOutlined style={{ color: token.colorTextQuaternary }} />
              ) : caretDirection === 'up' ? (
                <CaretUpOutlined style={{ color: token.colorSuccess }} />
              ) : (
                <CaretDownOutlined style={{ color: token.colorError }} />
              )
            }
            disabled={disabled}
            aria-label="sellprice-details-button"
          />
        </Popover>
      )}
    </FlexBox>
  );
};

export default SellPriceUI;
